/* Hallmark · component: RackPassportCard · genre: modern-minimal
 * states: default · hover · focus-visible · active · disabled
 * contrast: pass (40-41) · tokens: pass (48) · responsive: pass (34, 49)
 * Colors: project CSS tokens only (var(--bg-app), var(--border-color), var(--text-primary)).
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, LayoutGrid, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useConfirmStore } from '../../store/confirmStore';
import { api } from '../../utils/api';
import { PatchPanelMapping } from './PatchPanelMapping';
import { DeviceFaceplateViewer } from './DeviceFaceplateViewer';

interface Equipment {
  id: string | number;
  name: string;
  equipment_type: string;
  status?: string;
  u_position?: number;
  u_height?: number;
  mounting_position?: string;
  asset_tag?: string;
  serial_number?: string;
  weight_kg?: number;
  power_w?: number;
  airflow?: string;
  depth?: string;
  [key: string]: any;
}

interface RackPassportCardProps {
  selectedEq: Equipment;
  theme: 'dark' | 'light';
  actualCode: string;
  actualRackId: string | number | undefined;
  onBack: () => void;
  onEdit: (eq: Equipment) => void;
  liveMetrics?: { temp_c?: number; power_w?: number } | null;
}

/**
 * Shows full equipment passport with attribute grid, actions, and patch panel mapping.
 */
export function RackPassportCard({
  selectedEq,
  theme: _theme,
  actualCode,
  actualRackId,
  onBack,
  onEdit,
  liveMetrics,
}: RackPassportCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showFaceplateModal, setShowFaceplateModal] = useState(false);

  useEffect(() => {
    if (!showFaceplateModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFaceplateModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFaceplateModal]);

  const { data: portsData = [], isLoading: isPortsLoading } = useQuery<any[]>({
    queryKey: ['equipmentPorts', selectedEq?.id],
    queryFn: () => api.get(`/api/equipment/${selectedEq?.id}/ports`).then(r => r.data),
    enabled: showFaceplateModal && !!selectedEq?.id,
  });

  const { data: cablesData = [], isLoading: isCablesLoading } = useQuery<any[]>({
    queryKey: ['equipmentCables', selectedEq?.id],
    queryFn: () => api.get(`/api/equipment/${selectedEq?.id}/cables`).then(r => r.data).catch(() => []),
    enabled: showFaceplateModal && !!selectedEq?.id,
  });

  const handleDelete = async () => {
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление оборудования',
      message: `Вы действительно хотите удалить прибор "${selectedEq.name}"?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/equipment/${selectedEq.id}`);
      toast.success('Прибор удален');
      onBack();
      queryClient.invalidateQueries({ queryKey: ['rackEquipment', actualCode, actualRackId] });
    } catch {
      toast.error('Не удалось удалить прибор');
    }
  };

  const attrs = [
    {
      label: 'Позиция',
      value: selectedEq.is_zero_u
        ? `${selectedEq.mounting_position || 'Zero-U'} (0U)`
        : `U${selectedEq.u_position} (${selectedEq.u_height}U)`,
      accent: 'var(--accent)',
    },
    { label: 'Asset Tag', value: selectedEq.asset_tag || '—', accent: '' },
    { label: 'Serial', value: selectedEq.serial_number || '—', accent: '' },
    { label: 'Масса', value: `${selectedEq.weight_kg || 0} кг`, accent: 'var(--color-success, #10b981)' },
    { label: 'Мощность (Ном)', value: `${selectedEq.power_w || 0} Вт`, accent: 'var(--color-warning, #f59e0b)' },
    ...(liveMetrics?.power_w != null
      ? [{
          label: 'Мощность (Факт)',
          value: `${Math.round(liveMetrics.power_w)} Вт`,
          accent: 'var(--color-warning, #f59e0b)',
        }]
      : []),
    ...(liveMetrics?.temp_c != null
      ? [{
          label: 'Температура (Live)',
          value: `${liveMetrics.temp_c.toFixed(1)} °C`,
          accent: liveMetrics.temp_c > 32
            ? 'var(--status-critical)'
            : liveMetrics.temp_c > 27
            ? 'var(--status-warning)'
            : 'var(--color-success, #10b981)',
        }]
      : []),
    { label: 'Обдув', value: selectedEq.airflow || 'Front-to-Rear', accent: '' },
    { label: 'Глубина', value: selectedEq.depth || 'Full Depth', accent: '' },
  ];

  return (
    <div className="space-y-3">
      {/* Action Header */}
      <div
        className="flex justify-between items-center p-2 rounded-sm border"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        <button
          onClick={onBack}
          className="text-xs flex items-center gap-1 transition-colors"
          style={{ color: 'var(--text-muted)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
        >
          ← Обзор стойки
        </button>

        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setShowFaceplateModal(true)}
            data-testid="btn-open-faceplate"
            className="px-2 py-1 rounded-sm font-semibold transition-colors border text-[10px] flex items-center gap-1"
            style={{
              background: 'var(--bg-panel)',
              borderColor: 'var(--border-color)',
              color: 'var(--accent)',
            }}
            title="Открыть 2D фасадную панель и порты"
          >
            <LayoutGrid className="w-3 h-3" />
            Фасадная панель
          </button>

          <button
            onClick={() => onEdit(selectedEq)}
            className="px-2 py-1 rounded-sm font-semibold transition-colors border text-[10px]"
            style={{
              background: 'var(--bg-panel)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
          >
            Редактировать
          </button>

          <button
            onClick={() => {
              if (!actualCode) return;
              navigate(`/${actualCode}/equipment/${selectedEq.id}?fromRack=${actualRackId}`);
            }}
            className="px-2 py-1 rounded-sm font-semibold transition-colors text-[10px]"
            style={{
              background: 'var(--accent)',
              color: 'var(--text-on-accent, #ffffff)',
            }}
          >
            Профиль &amp; Порты ➤
          </button>

          <button
            onClick={handleDelete}
            className="p-1 rounded-sm transition-colors border"
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              borderColor: 'rgba(239, 68, 68, 0.3)',
              color: '#ef4444',
            }}
            title="Удалить прибор"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Passport Card */}
      <div
        className="border p-3 rounded-sm relative overflow-hidden"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        {/* Header: type badge + status + name */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="px-2 py-0.5 text-[10px] font-bold rounded-sm border uppercase tracking-wider"
                style={{
                  background: 'var(--bg-active)',
                  color: 'var(--accent)',
                  borderColor: 'var(--border-focus)',
                }}
              >
                {selectedEq.equipment_type}
              </span>
              <span
                className="px-2 py-0.5 text-[10px] font-bold rounded-sm border uppercase tracking-wider"
                style={{
                  background: selectedEq.status === 'Active'
                    ? 'rgba(16, 185, 129, 0.15)'
                    : selectedEq.status === 'Staged'
                    ? 'rgba(6, 182, 212, 0.15)'
                    : 'rgba(239, 68, 68, 0.15)',
                  color: selectedEq.status === 'Active'
                    ? '#10b981'
                    : selectedEq.status === 'Staged'
                    ? '#06b6d4'
                    : '#ef4444',
                  borderColor: selectedEq.status === 'Active'
                    ? 'rgba(16, 185, 129, 0.3)'
                    : selectedEq.status === 'Staged'
                    ? 'rgba(6, 182, 212, 0.3)'
                    : 'rgba(239, 68, 68, 0.3)',
                }}
              >
                ● {selectedEq.status || 'Active'}
              </span>
            </div>
            <h2 className="text-sm font-bold mt-1.5" style={{ color: 'var(--text-primary)' }}>
              {selectedEq.name}
            </h2>
            {(selectedEq.manufacturer || selectedEq.model) && (
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {[selectedEq.manufacturer, selectedEq.model].filter(Boolean).join(' ')}
              </p>
            )}
            <p className="text-[11px] font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>
              ID: {selectedEq.id}
            </p>
          </div>
        </div>

        {/* Attribute definition grid (borderless, eliminates card-in-card nesting) */}
        <dl
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t"
          style={{ borderColor: 'var(--border-color)' }}
        >
          {attrs.map(({ label, value, accent }) => (
            <div
              key={label}
              className="p-2 rounded-sm"
              style={{
                background: 'var(--bg-panel)',
              }}
            >
              <dt
                className="text-[9px] uppercase tracking-wider block font-medium"
                style={{ color: 'var(--text-muted)' }}
              >
                {label}
              </dt>
              <dd
                className="text-xs font-bold font-mono tabular-nums block truncate mt-0.5"
                style={{ color: accent || 'var(--text-primary)' }}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>

        {/* Patch Panel Front→Rear Mapping */}
        {selectedEq.equipment_type === 'patch_panel' && (
          <div
            className="border p-3 rounded-sm mt-3"
            style={{
              background: 'var(--bg-panel)',
              borderColor: 'var(--border-color)',
            }}
          >
            <PatchPanelMapping
              equipmentId={String(selectedEq.id)}
              equipmentName={selectedEq.name}
            />
          </div>
        )}
      </div>

      {/* Faceplate 2D Viewer Modal */}
      {showFaceplateModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="faceplate-modal-title"
          data-testid="modal-faceplate-viewer"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowFaceplateModal(false)}
        >
          <div
            className="relative w-full max-w-4xl border rounded-lg shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{
              background: 'var(--bg-panel)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex justify-between items-center mb-4 pb-2 border-b"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                <h3 id="faceplate-modal-title" className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
                  Интерактивная панель оборудования: {selectedEq.name}
                </h3>
              </div>
              <button
                onClick={() => setShowFaceplateModal(false)}
                className="min-h-[36px] min-w-[36px] flex items-center justify-center rounded transition-colors focus-visible:outline focus-visible:outline-2"
                style={{
                  color: 'var(--text-muted)',
                  ['--tw-outline-color' as string]: 'var(--border-focus)',
                }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                data-testid="btn-close-faceplate-modal"
                title="Закрыть"
                aria-label="Закрыть модальное окно"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <DeviceFaceplateViewer
              device={{
                ...selectedEq,
                id: String(selectedEq.id),
                u_height: selectedEq.u_height || 1,
                ports: portsData,
              }}
              cables={cablesData}
              isLoading={isPortsLoading || isCablesLoading}
              dcCode={actualCode}
            />
          </div>
        </div>
      )}
    </div>
  );
}
